import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";

export default function NewPart({ onBack, onOpenEditPart }) {
  const dialog = useAppDialog();

  const [partName, setPartName] = useState("");
  const [revMaj, setRevMaj] = useState("");
  const [revMin, setRevMin] = useState("");
  const [loadingAuto, setLoadingAuto] = useState(false);
  const [loadingContinue, setLoadingContinue] = useState(false);
  const [pageError, setPageError] = useState("");

  const reviewSummary = useMemo(() => {
    return {
      partName: partName || "Not entered",
      printRevision: revMaj || "Not entered",
      mafiaRevision: revMin || "Not entered",
      nextStep: "Edit Part",
    };
  }, [partName, revMaj, revMin]);

  async function handleAutoClick() {
    if (partName.trim() === "" || revMaj.trim() === "") {
      await dialog.warning(
        "Please ensure part name and print rev is entered before selecting auto."
      );
      return;
    }

    let parsedRevMaj = 0;
    try {
      parsedRevMaj = Number.parseInt(revMaj.trim(), 10);
      if (Number.isNaN(parsedRevMaj)) {
        throw new Error("Invalid print rev");
      }
    } catch {
      await dialog.warning("Please enter a proper print rev before selecting auto.");
      return;
    }

    try {
      setLoadingAuto(true);
      setPageError("");

      const autoRev = await getAutoMafiaRev({
        partNum: partName.trim(),
        rev: parsedRevMaj,
      });
      setRevMin(String(autoRev));
    } catch (error) {
      console.error("GetAutoMafiaRev failed:", error);
      setPageError("Failed to get automatic MAFIA revision.");
      await dialog.error("Failed to get automatic MAFIA revision.");
    } finally {
      setLoadingAuto(false);
    }
  }

  async function handleContinueClick() {
    if (partName.trim() === "") {
      await dialog.warning("Please ensure part name is entered before continuing.");
      return;
    }

    let parsedRevMaj = 0;
    let parsedRevMin = 0;

    try {
      parsedRevMaj = Number.parseInt(revMaj.trim(), 10);
      parsedRevMin = Number.parseInt(revMin.trim(), 10);

      if (Number.isNaN(parsedRevMaj) || Number.isNaN(parsedRevMin)) {
        throw new Error("Invalid rev numbers");
      }
    } catch {
      await dialog.warning(
        "Please enter a proper print and MAFIA rev number before continuing."
      );
      return;
    }

    try {
      setLoadingContinue(true);
      setPageError("");

      const exists = await getPartExistsExact({
        partNumber: partName.trim(),
        rev: parsedRevMaj,
        revMinor: parsedRevMin,
      });

      if (exists) {
        await dialog.warning(
          "This part already exists within the database. Cannot create new part with the information you have supplied. If you meant to edit this part, please select 'Edit Part' from the menu."
        );
        return;
      }

      if (typeof onOpenEditPart === "function") {
        onOpenEditPart({
          partNum: partName.trim().toUpperCase(),
          rev: parsedRevMaj,
          revMinor: parsedRevMin,
          isImported: false,
          isNew: true,
        });
        return;
      }

      await dialog.error("Edit Part callback is not configured.");
    } catch (error) {
      console.error("Continue failed:", error);
      setPageError("Failed to continue to Edit Part.");
      await dialog.error("Failed to continue to Edit Part.");
    } finally {
      setLoadingContinue(false);
    }
  }

  return (
    <div className="new-part-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-add-circle-line"
        eyebrow="MAFIA Aux"
        title="Create New MAFIA"
        subtitle="Create a new MAFIA record manually. Enter the part name and revisions, then continue to Edit Part."
        tone="rose"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Create New MAFIA" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="new-part-toolbar-main">
                <div className="new-part-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>
              </div>

              <div className="new-part-toolbar-utility">
                <Button
                  color="primary"
                  className="new-part-action-btn new-part-utility-btn"
                  onClick={handleAutoClick}
                  disabled={loadingAuto || loadingContinue}
                >
                  <i className="ri-magic-line new-part-btn-icon me-1" />
                  {loadingAuto ? "Loading Auto Rev..." : "Auto MAFIA Rev"}
                </Button>

                <Button
                  color="success"
                  className="new-part-action-btn new-part-utility-btn"
                  onClick={handleContinueClick}
                  disabled={loadingContinue || loadingAuto}
                >
                  <i className="ri-arrow-right-circle-line new-part-btn-icon me-1" />
                  {loadingContinue ? "Opening..." : "Continue to Edit Part"}
                </Button>

                <div className="new-part-info-pill">
                  <span className="new-part-info-label">Part Name</span>
                  <span className="new-part-info-value" title={partName || "Not entered"}>
                    {partName || "Not entered"}
                  </span>
                </div>

                <div className="new-part-info-pill">
                  <span className="new-part-info-label">Drawing Revision</span>
                  <span className="new-part-info-value">{revMaj || "—"}</span>
                </div>

                <div className="new-part-info-pill">
                  <span className="new-part-info-label">MAFIA Revision</span>
                  <span className="new-part-info-value">{revMin || "—"}</span>
                </div>
              </div>

              {pageError ? (
                <div className="new-part-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <Row className="g-4">
                <Col xl={7}>
                  <div className="new-part-panel-card h-100">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h5 className="mb-1">Part Setup</h5>
                        <p className="text-muted mb-0">
                          Provide the required values before moving into the main editing workspace.
                        </p>
                      </div>
                      <Badge color="light" className="new-part-soft-badge text-uppercase">
                        Setup
                      </Badge>
                    </div>

                    <Row className="g-3">
                      <Col md={12}>
                        <Field
                          label="Part Name"
                          value={partName}
                          onChange={setPartName}
                          placeholder="Enter part name"
                          icon="ri-box-3-line"
                        />
                      </Col>
                      <Col md={6}>
                        <Field
                          label="Drawing Revision"
                          value={revMaj}
                          onChange={setRevMaj}
                          placeholder="Enter print revision"
                          icon="ri-file-edit-line"
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">MAFIA Revision</Label>
                        <InputGroup>
                          <InputGroupText className="bg-light border-end-0">
                            <i className="ri-hashtag" />
                          </InputGroupText>
                          <Input
                            type="text"
                            value={revMin}
                            onChange={(e) => setRevMin(e.target.value)}
                            placeholder="Enter MAFIA revision"
                            className="border-start-0"
                          />
                        </InputGroup>
                        <div className="new-part-panel-note mt-2">
                          Use Auto to calculate the next available MAFIA revision for the selected
                          part and print revision.
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>

                <Col xl={5}>
                  <div className="new-part-panel-card h-100">
                    <h5 className="mb-3">Review Summary</h5>
                    <div className="new-part-review-list">
                      <div className="new-part-review-item">
                        <span>Part Name</span>
                        <strong>{reviewSummary.partName}</strong>
                      </div>
                      <div className="new-part-review-item">
                        <span>Drawing Revision</span>
                        <strong>{reviewSummary.printRevision}</strong>
                      </div>
                      <div className="new-part-review-item">
                        <span>MAFIA Revision</span>
                        <strong>{reviewSummary.mafiaRevision}</strong>
                      </div>
                      <div className="new-part-review-item">
                        <span>Next Step</span>
                        <strong>{reviewSummary.nextStep}</strong>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <Button
                        color="primary"
                        className="new-part-action-btn"
                        onClick={handleAutoClick}
                        disabled={loadingAuto || loadingContinue}
                      >
                        <i className="ri-magic-line new-part-btn-icon me-1" />
                        {loadingAuto ? "Loading..." : "Auto"}
                      </Button>
                      <Button
                        color="success"
                        className="new-part-action-btn"
                        onClick={handleContinueClick}
                        disabled={loadingContinue || loadingAuto}
                      >
                        <i className="ri-arrow-right-circle-line new-part-btn-icon me-1" />
                        {loadingContinue ? "Opening..." : "Continue"}
                      </Button>
                      <Button
                        color="light"
                        className="new-part-action-btn border"
                        onClick={onBack}
                      >
                        <i className="ri-close-line new-part-btn-icon me-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .new-part-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .new-part-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .new-part-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .new-part-soft-badge {
          border: 1px solid #e9ebec;
          padding: 0.55rem 0.85rem;
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          border-radius: 999px;
        }

        .new-part-panel-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
          height: 100%;
        }

        .new-part-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .new-part-action-btn:disabled,
          .new-part-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .new-part-utility-btn {
            min-width: 110px;
          }

        .new-part-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .new-part-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .new-part-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .new-part-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .new-part-error-banner {
          display: flex;
          align-items: center;
          padding: 0.8rem 0.95rem;
          border-radius: 0.8rem;
          border: 1px solid rgba(220, 53, 69, 0.18);
          background: rgba(220, 53, 69, 0.06);
          color: #842029;
          font-weight: 500;
        }

        .new-part-panel-note {
          color: #878a99;
          font-size: 0.92rem;
        }

        .new-part-review-list {
          display: grid;
          gap: 0.8rem;
        }

        .new-part-review-item {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          align-items: center;
          padding: 0.8rem 0.9rem;
          border-radius: 0.9rem;
          background: #f8fafc;
          border: 1px solid #eef2f7;
        }

        .new-part-review-item span {
          color: #878a99;
          font-size: 0.92rem;
        }

        .new-part-review-item strong {
          color: #212529;
          text-align: right;
          word-break: break-word;
        }

        @media (max-width: 991.98px) {
          .new-part-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .new-part-toolbar-utility {
            align-items: stretch;
          }

          .new-part-review-item {
            align-items: flex-start;
            flex-direction: column;
          }

          .new-part-review-item strong {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, icon }) {
  return (
    <div>
      <Label className="form-label">{label}</Label>
      <InputGroup>
        <InputGroupText className="bg-light border-end-0">
          <i className={icon} />
        </InputGroupText>
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="border-start-0"
        />
      </InputGroup>
    </div>
  );
}

async function getAutoMafiaRev(request) {
  const response = await api.post("mafia/auto-mafiarev", {
    partNum: request.partNum,
    rev: request.rev,
  });

  return response.data;
}

async function getPartExistsExact(request) {
  const response = await api.post("mafia/part-exists", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  return response.data;
}